import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StressTestingPanel = ({ scenarios, results }) => {
  const [selectedScenario, setSelectedScenario] = useState('market_crash');
  const [isRunning, setIsRunning] = useState(false);

  const runStressTest = async (scenarioId) => {
    setIsRunning(true);
    setSelectedScenario(scenarioId);
    
    // Simulate stress test execution
    setTimeout(() => {
      setIsRunning(false);
    }, 3000);
  };

  const getScenarioIcon = (type) => {
    switch (type) {
      case 'market_crash': return 'TrendingDown';
      case 'interest_rate': return 'Percent';
      case 'currency': return 'DollarSign';
      case 'volatility': return 'Activity';
      case 'liquidity': return 'Droplets';
      default: return 'AlertTriangle';
    }
  };

  const getImpactColor = (impact) => {
    if (impact < -10) return 'text-error';
    if (impact < -5) return 'text-warning';
    if (impact < 0) return 'text-accent';
    return 'text-success';
  };

  const getCurrentResults = () => {
    return results?.[selectedScenario] || results?.market_crash;
  };

  const currentResults = getCurrentResults();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium text-popover-foreground">{label}</p>
          <p className="text-sm text-muted-foreground">
            Impact: {payload?.[0]?.value > 0 ? '+' : ''}{payload?.[0]?.value?.toFixed(2)}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Stress Testing & Scenario Analysis</h3>
        <div className="flex items-center space-x-2">
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs ${
            isRunning ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
          }`}>
            <div className={`w-2 h-2 rounded-full ${
              isRunning ? 'bg-warning animate-pulse-subtle' : 'bg-success'
            }`}></div>
            <span>{isRunning ? 'Running Test...' : 'Ready'}</span>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Play" 
            iconSize={14}
            loading={isRunning}
            onClick={() => runStressTest(selectedScenario)}
          >
            Run Test
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Stress Scenarios</h4>
          {scenarios?.map((scenario) => (
            <button
              key={scenario?.id}
              onClick={() => setSelectedScenario(scenario?.id)}
              className={`w-full text-left p-3 rounded-lg transition-smooth ${
                selectedScenario === scenario?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon name={getScenarioIcon(scenario?.type)} size={16} />
                <div className="flex-1">
                  <div className="font-medium text-sm">{scenario?.name}</div>
                  <div className="text-xs opacity-75">{scenario?.description}</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="TrendingDown" size={16} className="text-error" />
                <span className="text-sm font-medium text-foreground">Portfolio Impact</span>
              </div>
              <div className={`text-2xl font-bold ${getImpactColor(currentResults?.portfolioImpact)}`}>
                {currentResults?.portfolioImpact > 0 ? '+' : ''}{currentResults?.portfolioImpact?.toFixed(2)}%
              </div>
              <div className="text-xs text-muted-foreground">
                ₹{Math.abs(currentResults?.portfolioImpact * 500000)?.toLocaleString('en-IN')}
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Shield" size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">Max Drawdown</span>
              </div>
              <div className="text-2xl font-bold text-error">
                {currentResults?.maxDrawdown?.toFixed(2)}%
              </div>
              <div className="text-xs text-muted-foreground">
                Worst case scenario
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={16} className="text-accent" />
                <span className="text-sm font-medium text-foreground">Recovery Time</span>
              </div>
              <div className="text-2xl font-bold text-accent">
                {currentResults?.recoveryDays}
              </div>
              <div className="text-xs text-muted-foreground">
                Days to recover
              </div>
            </div>

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Target" size={16} className="text-success" />
                <span className="text-sm font-medium text-foreground">Confidence</span>
              </div>
              <div className="text-2xl font-bold text-success">
                {currentResults?.confidence}%
              </div>
              <div className="text-xs text-muted-foreground">
                Model accuracy
              </div>
            </div>
          </div>

          <div className="h-64">
            <h4 className="text-sm font-semibold text-foreground mb-3">Asset Impact Breakdown</h4>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={currentResults?.assetBreakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="asset" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="impact" 
                  fill="var(--color-error)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Risk Mitigation Recommendations</h4>
            <div className="space-y-2">
              {currentResults?.recommendations?.map((recommendation, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
                  <Icon name="Lightbulb" size={16} className="text-warning mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">{recommendation?.title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{recommendation?.description}</div>
                  </div>
                  <div className={`text-xs px-2 py-1 rounded ${
                    recommendation?.priority === 'high' ? 'bg-error/10 text-error' :
                    recommendation?.priority === 'medium'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                  }`}>
                    {recommendation?.priority}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-border">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Last Run:</span>
            <span className="ml-2 font-medium text-foreground">
              {new Date()?.toLocaleString('en-IN')}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Scenarios:</span>
            <span className="ml-2 font-medium text-foreground">{scenarios?.length} available</span>
          </div>
          <div>
            <span className="text-muted-foreground">Model Version:</span>
            <span className="ml-2 font-medium text-foreground">v2.1.3</span>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" iconName="Download" iconSize={14}>
              Export Results
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StressTestingPanel;